import { startRegistration, startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';

const SERVERS = {
  cloud: process.env.REACT_APP_API_URL       || 'https://api.marvyitservices.com/api',
  local: process.env.REACT_APP_LOCAL_API_URL || 'https://api.marvyitservices.com/api',
};

export function getServerMode() {
  // Default to 'local' (Oracle + real Socket.IO) rather than 'cloud' (the
  // Cloudflare Worker, which has no persistent-connection support and
  // relies on polling that mobile PWAs suspend in the background) --
  // regular employees never see the mode toggle at all (Settings is
  // admin-only), so this fallback is what decides their experience.
  try { return localStorage.getItem('marvy_server') || 'local'; } catch { return 'local'; }
}

export function setServerMode(mode) {
  try { localStorage.setItem('marvy_server', mode); } catch {}
}

function getBase() {
  return SERVERS[getServerMode()] || SERVERS.cloud;
}

export function getSocketBase() {
  return getBase().replace(/\/api\/?$/, '');
}

function getToken() {
  try { return localStorage.getItem('marvy_token'); } catch { return null; }
}

export function getAuthToken() {
  return getToken();
}

async function request(method, path, body) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${getBase()}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let data;
  try { data = await res.json(); } catch { data = {}; }

  if (res.status === 401 && token) {
    // Only redirect on session expiry (had a token, server rejected it)
    localStorage.removeItem('marvy_token');
    localStorage.removeItem('marvy_user');
    window.location.href = '/login';
    throw new Error('Session expired. Please log in again.');
  }

  if (!res.ok) {
    throw new Error(data.error || `Request failed: ${res.status}`);
  }
  return data;
}

const api = {
  // Auth
  login:          (email, password) => request('POST', '/auth/login', { email, password }),
  logout:         ()                => request('POST', '/auth/logout'),
  createOidcSession: async () => {
    const token = getToken();
    const res = await fetch(`${getBase()}/auth/oidc-session`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const data = await res.json().catch(() => ({}));
    return data.sessionToken || null;
  },
  oidcLogout: async () => {
    try {
      await fetch(`${getBase()}/auth/oidc-logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {}
    try {
      await fetch('https://ai.marvyitservices.com/api/v1/auths/signout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {}
  },
  me:             ()                => request('GET',  '/auth/me'),
  register:       (body)            => request('POST', '/auth/register', body),
  forgotPassword: (email)           => request('POST', '/auth/forgot-password', { email }),
  resetPassword:  (token, password) => request('POST', '/auth/reset-password', { token, password }),

  // Projects
  getProjects:     ()    => request('GET', '/projects'),
  getAllProjects:   ()    => request('GET', '/projects?all=true'),

  // Timesheets
  getTimesheets: ()               => request('GET',  '/timesheets'),
  getStats:      ()               => request('GET',  '/timesheets/stats'),
  getTimesheet:  (id)             => request('GET',  `/timesheets/${id}`),
  saveTimesheet: (body)           => request('POST', '/timesheets', body),
  approveTimesheet: (id, body)    => request('PATCH',  `/timesheets/${id}/approve`, body),
  rejectTimesheet:  (id, body)    => request('PATCH',  `/timesheets/${id}/reject`,  body),
  deleteTimesheet:  (id)          => request('DELETE', `/timesheets/${id}`),

  // Users (admin)
  getUsers:     ()         => request('GET',    '/users'),
  getUser:      (id)       => request('GET',    `/users/${id}`),
  createUser:   (body)     => request('POST',   '/users', body),
  updateUser:   (id, body) => request('PUT',    `/users/${id}`, body),
  deleteUser:   (id)       => request('DELETE', `/users/${id}`),
  getUserChatContacts:    (id)       => request('GET', `/users/${id}/chat-contacts`),
  updateUserChatContacts: (id, body) => request('PUT', `/users/${id}/chat-contacts`, body),

  // Projects (admin)
  createProject: (body)     => request('POST',   '/projects', body),
  updateProject: (id, body) => request('PUT',    `/projects/${id}`, body),
  deleteProject: (id)       => request('DELETE', `/projects/${id}`),

  // Holidays
  getHolidays:        (year)     => request('GET',    `/holidays${year ? `?year=${year}` : ''}`),
  getUpcomingHolidays:()         => request('GET',    '/holidays/upcoming'),
  createHoliday:      (body)     => request('POST',   '/holidays', body),
  updateHoliday:      (id, body) => request('PUT',    `/holidays/${id}`, body),
  deleteHoliday:      (id)       => request('DELETE', `/holidays/${id}`),

  // Leaves
  getLeaves:     ()         => request('GET',    '/leaves'),
  submitLeave:   (body)     => request('POST',   '/leaves', body),
  cancelLeave:   (id)       => request('DELETE', `/leaves/${id}`),
  approveLeave:  (id, body) => request('PATCH',  `/leaves/${id}/approve`, body),
  rejectLeave:   (id, body) => request('PATCH',  `/leaves/${id}/reject`,  body),

  // Settings (admin)
  getSettings:      ()            => request('GET',   '/settings'),
  updateSetting:    (key, value)  => request('PATCH', '/settings', { key, value }),
  getStorageStats:  ()            => request('GET',   '/settings/storage-stats'),
  getSessionTimeout:()            => request('GET',   '/settings/session-timeout'),

  // Timesheet attachment download URL (used as href)
  attachmentUrl: (timesheetId) => `${getBase()}/timesheets/${timesheetId}/attachment`,

  // Client timesheet parser
  parseClientHours: async (file) => {
    const token = getToken();
    const form  = new FormData();
    form.append('file', file);
    const r = await fetch(`${getBase()}/parse-hours`, {
      method:  'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body:    form,
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error || 'Parse failed');
    return d;
  },

  // Reports
  getReportByEmployee:   (period) => request('GET', `/reports/hours-by-employee?period=${period}`),
  getReportByProject:    (period) => request('GET', `/reports/hours-by-project?period=${period}`),
  getTimesheetSummary:   ()       => request('GET', '/reports/timesheet-summary'),
  getLeaveSummary:       ()       => request('GET', '/reports/leave-summary'),
  getReportExport:       (period) => request('GET', `/reports/export?period=${period}`),

  // Roles (admin)
  getRoles:           ()                  => request('GET', '/roles'),
  updateRolePermissions: (name, body)     => request('PUT', `/roles/${name}`, body),

  // Positions
  getPositions:    ()         => request('GET',    '/positions'),
  createPosition:  (body)     => request('POST',   '/positions', body),
  updatePosition:  (id, body) => request('PUT',    `/positions/${id}`, body),
  deletePosition:  (id)       => request('DELETE', `/positions/${id}`),

  // Clients (admin)
  getClients:    ()         => request('GET',    '/clients'),
  createClient:  (body)     => request('POST',   '/clients', body),
  updateClient:  (id, body) => request('PUT',    `/clients/${id}`, body),
  deleteClient:  (id)       => request('DELETE', `/clients/${id}`),

  // Consultant Rates (admin)
  getConsultantRates:    ()         => request('GET',    '/consultant-rates'),
  createConsultantRate:  (body)     => request('POST',   '/consultant-rates', body),
  updateConsultantRate:  (id, body) => request('PUT',    `/consultant-rates/${id}`, body),
  deleteConsultantRate:  (id)       => request('DELETE', `/consultant-rates/${id}`),

  // Invoices (admin)
  getInvoices:     ()         => request('GET',    '/invoices'),
  previewInvoice:  (body)     => request('POST',   '/invoices/preview', body),
  deleteInvoice:   (id)       => request('DELETE', `/invoices/${id}`),
  sendInvoice:          (id, body) => request('POST', `/invoices/${id}/send`, body),
  getInvoiceSends:      (id)       => request('GET',  `/invoices/${id}/sends`),
  getInvoiceAttachments:      (id) => request('GET', `/invoices/${id}/attachments`),
  getInvoiceEmployeeTimesheets:(id) => request('GET', `/invoices/${id}/employee-timesheets`),
  invoicePdfUrl:   (id)       => `${getBase()}/invoices/${id}/pdf`,

  // Chat
  getChatEmployees:      ()               => request('GET',   '/chat/employees'),
  getConversations:      ()               => request('GET',   '/chat/conversations'),
  startConversation:     (userId)         => request('POST',  '/chat/conversations', { userId }),
  getMessages:           (conversationId) => request('GET',   `/chat/conversations/${conversationId}/messages`),
  sendMessage:           (conversationId, body) => request('POST', `/chat/conversations/${conversationId}/messages`, { body }),
  markConversationRead:  (conversationId) => request('PATCH', `/chat/conversations/${conversationId}/read`),
  deleteMessage:         (conversationId, messageId) => request('DELETE', `/chat/conversations/${conversationId}/messages/${messageId}`),
  clearConversation:     (conversationId) => request('DELETE', `/chat/conversations/${conversationId}`),

  // Passkeys (Face ID)
  passkeySupported:     () => browserSupportsWebAuthn(),
  getMyPasskeys:        () => request('GET', '/passkey/credentials'),
  deleteMyPasskey:      (credentialId) => request('DELETE', `/passkey/credentials/${credentialId}`),
  registerPasskey: async (nickname) => {
    const { ticket, options } = await request('POST', '/passkey/register/options');
    const response = await startRegistration({ optionsJSON: options });
    return request('POST', '/passkey/register/verify', { ticket, response, nickname });
  },
  loginWithPasskey: async () => {
    const { ticket, options } = await request('POST', '/passkey/login/options');
    const response = await startAuthentication({ optionsJSON: options });
    return request('POST', '/passkey/login/verify', { ticket, response });
  },
  getAllPasskeys: () => request('GET', '/passkey/admin/all'),
  revokePasskey:  (credentialId) => request('DELETE', `/passkey/admin/${credentialId}`),

  // WebRTC calling (Oracle mode only)
  getTurnCredentials: () => request('GET', '/webrtc/turn-credentials'),

  // Resume Booster (admin)
  getResumeTemplates: () => request('GET', '/resume-tailor/templates'),
  tailorResume: async (file, jobDescription, extraInstructions, template) => {
    const token = getToken();
    const form  = new FormData();
    form.append('resume', file);
    form.append('jobDescription', jobDescription);
    if (extraInstructions) form.append('extraInstructions', extraInstructions);
    if (template) form.append('template', template);
    const r = await fetch(`${getBase()}/resume-tailor`, {
      method:  'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body:    form,
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || 'Resume tailoring failed');
    return d;
  },
};

export default api;
